import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useCourseData } from '../hooks/useCourseData';
import { useContentCourse } from '../context/ContentContext';
import apiBackoffice from '../../../../services/apiBackoffice';
import { useToast } from '../../../../hooks/toast';
import { useAdminDelete } from '../../Admin/hooks/useAdminDelete';
import {
  ContentEditorLayout,
  Confirm,
  useContentEditor,
} from '../../../../components/Admin';
import Field from '../../../../components/Admin/Field';
import { TextInput, TextArea } from '../../../../components/Admin/Field/styles';
import OptionsEditor from './OptionsEditor';
import { parseOptions, stripOptionLetter, findCorrectIndex } from '../utils/options';

const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

interface QuizValues {
  question: string;
  videoposition: string;
  options: string[];
  correctIndex: number;
}

const EMPTY: QuizValues = {
  question: '',
  videoposition: '00:00',
  options: ['', '', '', ''],
  correctIndex: 0,
};

interface QuizItem {
  quizid: string;
  question?: string;
  /** String JSON no backend (ex.: '["A) texto"]'), mas aceitamos array também. */
  options?: string | string[];
  answer?: string;
  videoposition?: string;
}

const QuizForm: React.FC = () => {
  const { addToast } = useToast();
  const { courseId } = useContentCourse();
  const {
    seasons,
    movies,
    loadingSeasons,
    loadingMovies,
    selectedCourseId,
    selectedSeasonId,
    selectedMovieId,
    handleSeasonChange,
    handleMovieChange,
  } = useCourseData(courseId);
  const { deleteItem, deletingId } = useAdminDelete();

  const { values, setField, selectedId, editing, dirty, select, clear, markSaved } =
    useContentEditor<QuizValues>(EMPTY);

  const [quizzes, setQuizzes] = useState<QuizItem[]>([]);
  const [loadingQuizzes, setLoadingQuizzes] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const moduleOptions = useMemo(
    () =>
      seasons.map((season) => ({
        value: season.seasonid,
        label: season.title,
        hint: season.seasonid,
      })),
    [seasons],
  );

  const lessonOptions = useMemo(
    () =>
      movies.map((movie) => ({
        value: movie.movieid,
        label: movie.title,
        hint: `posição ${movie.position ?? '-'}`,
      })),
    [movies],
  );

  // Os quizzes vêm embutidos na resposta de /course/season/movie (campo `quizzes`).
  const loadQuizzes = useCallback(async () => {
    if (!selectedCourseId || !selectedSeasonId || !selectedMovieId) {
      setQuizzes([]);
      return;
    }

    setLoadingQuizzes(true);
    try {
      const response = await apiBackoffice.get<any>('/course/season/movie', {
        params: {
          courseid: selectedCourseId,
          seasonid: selectedSeasonId,
        },
      });

      const list: any[] = Array.isArray(response.data)
        ? response.data
        : response.data?.movies ?? [];

      const movie = list.find(
        (item) => String(item.movieid) === String(selectedMovieId),
      );

      setQuizzes(Array.isArray(movie?.quizzes) ? movie.quizzes : []);
    } catch {
      setQuizzes([]);
    } finally {
      setLoadingQuizzes(false);
    }
  }, [selectedCourseId, selectedSeasonId, selectedMovieId]);

  useEffect(() => {
    loadQuizzes();
  }, [loadQuizzes]);

  const pickerItems = useMemo(
    () =>
      quizzes.map((quiz) => ({
        id: quiz.quizid,
        label: quiz.question || `Quiz ${quiz.quizid}`,
        hint: `nº ${quiz.quizid}${quiz.videoposition ? ` · ${quiz.videoposition}` : ''}`,
      })),
    [quizzes],
  );

  const onModuleChange = (seasonId: string) => {
    clear();
    handleSeasonChange(seasonId, selectedCourseId);
  };

  const onLessonChange = (movieId: string) => {
    clear();
    handleMovieChange(movieId);
  };

  const onSelectItem = (quizId: string) => {
    const quiz = quizzes.find((item) => item.quizid === quizId);
    if (!quiz) return;

    // `options` chega como string JSON do backend — parseOptions aceita os dois formatos.
    const parsed = parseOptions(quiz.options).map(stripOptionLetter);
    const correctIndex = findCorrectIndex(parseOptions(quiz.options), quiz.answer);

    select(quizId, {
      question: quiz.question ?? '',
      videoposition: quiz.videoposition ?? '00:00',
      options: parsed.length >= 2 ? parsed : ['', '', '', ''],
      correctIndex,
    });
  };

  const onChangeOption = (index: number, value: string) => {
    const next = [...values.options];
    next[index] = value;
    setField('options', next);
  };

  const onAddOption = () => {
    if (values.options.length < LETTERS.length) {
      setField('options', [...values.options, '']);
    }
  };

  const onRemoveOption = (index: number) => {
    if (values.options.length <= 2) {
      addToast({
        type: 'error',
        title: 'Atenção',
        description: 'Um quiz precisa de pelo menos 2 alternativas.',
      });
      return;
    }

    const next = values.options.filter((_, position) => position !== index);
    setField('options', next);
    if (values.correctIndex >= next.length) setField('correctIndex', 0);
  };

  const validate = () => {
    if (!selectedCourseId || !selectedSeasonId || !selectedMovieId) {
      addToast({
        type: 'error',
        title: 'Selecione módulo e aula',
        description: 'O quiz aparece dentro de uma aula — escolha módulo e aula antes.',
      });
      return false;
    }
    if (!values.question.trim()) {
      addToast({ type: 'error', title: 'Pergunta obrigatória' });
      return false;
    }
    if (values.options.some((option) => !option.trim())) {
      addToast({
        type: 'error',
        title: 'Alternativas incompletas',
        description: 'Preencha o texto de todas as alternativas.',
      });
      return false;
    }
    return true;
  };

  const persist = async (quizId: string) => {
    const formattedOptions = values.options.map(
      (option, index) => `${LETTERS[index]}) ${option.trim()}`,
    );

    const body = {
      courseid_seasonid_movieid_quizid: `${selectedCourseId}_${selectedSeasonId}_${selectedMovieId}_${quizId}`,
      courseid: selectedCourseId,
      seasonid: selectedSeasonId,
      movieid: String(selectedMovieId),
      quizid: String(quizId),
      title: selectedCourseId,
      question: values.question.trim(),
      answer: formattedOptions[values.correctIndex],
      options: formattedOptions,
      videoposition: values.videoposition.trim() || '00:00',
    };

    setSaving(true);
    try {
      const response = await apiBackoffice.post('/backoffice/quiz/add', body);
      const ok =
        response.status === 200 || response.data === 'OK' || response.data?.message === 'OK';

      if (!ok) throw new Error('O servidor não confirmou a gravação.');

      addToast({ type: 'success', title: editing ? 'Quiz atualizado' : 'Quiz cadastrado' });
      markSaved();
      await loadQuizzes();
      return true;
    } catch (err: any) {
      addToast({
        type: 'error',
        title: 'Erro ao salvar o quiz',
        description: err?.response?.data?.error || err?.message || 'Tente novamente.',
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const onSave = async () => {
    if (!validate()) return;

    if (editing) {
      await persist(selectedId);
      return;
    }

    const nextId = quizzes.reduce((max, item) => Math.max(max, Number(item.quizid) || 0), 0) + 1;
    const ok = await persist(String(nextId));
    if (ok) clear();
  };

  const onDelete = async () => {
    setConfirmDelete(false);
    if (!selectedId) return;

    const done = await deleteItem({
      entity: 'quiz',
      id: `${selectedCourseId}_${selectedSeasonId}_${selectedMovieId}_${selectedId}`,
      successTitle: 'Quiz excluído',
    });

    if (done) {
      clear();
      await loadQuizzes();
    }
  };

  return (
    <>
      <ContentEditorLayout
        moduleOptions={moduleOptions}
        moduleId={selectedSeasonId}
        onModuleChange={onModuleChange}
        modulesLoading={loadingSeasons}
        lessonOptions={lessonOptions}
        lessonId={selectedMovieId}
        onLessonChange={onLessonChange}
        lessonsLoading={loadingMovies}
        pickerItems={pickerItems}
        pickerTitle="Quizzes desta aula"
        selectedItemId={selectedId}
        onSelectItem={onSelectItem}
        pickerLoading={loadingQuizzes}
        editing={editing}
        dirty={dirty}
        saving={saving}
        deleting={!!deletingId}
        onSave={onSave}
        onEdit={onSave}
        onDelete={() => setConfirmDelete(true)}
        onClear={clear}
      >
        <Field label="Pergunta do quiz" required full>
          <TextArea
            value={values.question}
            onChange={(event) => setField('question', event.target.value)}
            placeholder="Ex.: O que o professor acabou de explicar sobre este tópico?"
          />
        </Field>
        <Field label="Momento no vídeo" required hint="MM:SS ou HH:MM:SS">
          <TextInput
            value={values.videoposition}
            onChange={(event) => setField('videoposition', event.target.value)}
            placeholder="05:30"
          />
        </Field>

        <Field label="Alternativas" required full>
          <OptionsEditor
            name="quiz-correct-option"
            options={values.options}
            correctIndex={values.correctIndex}
            onChangeOption={onChangeOption}
            onChangeCorrect={(index) => setField('correctIndex', index)}
            onAdd={onAddOption}
            onRemove={onRemoveOption}
          />
        </Field>
      </ContentEditorLayout>

      <Confirm
        open={confirmDelete}
        danger
        title="Excluir quiz"
        description="O quiz some da aula para os alunos. Não dá para desfazer."
        confirmLabel="Excluir"
        loading={!!deletingId}
        onConfirm={onDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </>
  );
};

export default QuizForm;
